import type React from "react";
import type { AriaAttributes } from "react";
import { useCallback, useRef, useState } from "react";
import { clamp } from "@common/helpers/collections";
import { styleWithVars } from "@lib/cssVars";
import type {
    BarSpan,
    BarTap,
    DensityChartPeriod,
    DensityChartPeriodLabel,
    DensityChartWindow,
} from "./DensityChart.lib";
import { barTapAtPoint } from "./DensityChart.lib";
import type { DensityChartBucket } from "./densityBuckets.lib";
import { useElementWidth } from "./useElementWidth";
import type { WindowDragStrip } from "./useDensityStrip";
import { useWindowDrag } from "./useWindowDrag";
import {
    toWindowPart,
    WINDOW_PART_ATTRIBUTE,
    WINDOW_STROKE_PX,
} from "./windowOverlay.lib";
import type { WindowPart } from "./windowSpan.lib";
import { spanToPeriod } from "./windowSpan.lib";
import styles from "./DensityChartWindowOverlay.module.scss";

interface DensityChartWindowOverlayProps {
    buckets: DensityChartBucket[];
    windowSpan: BarSpan;
    /** The earliest the window may start, after a zone before the start; partway into a bar when history starts mid-week or mid-month. */
    minBar: number;
    /** The strip the window pans while dragged past its visible edges. */
    strip: WindowDragStrip;
    /** The part of the bars in view, in px from their left, to keep the tooltip in. */
    visible: { start: number; end: number };
    onWindowChange: (window: DensityChartWindow) => void;
    /** Where a drop would land while dragging, then null. */
    onWindowPreview: (window: DensityChartWindow | null) => void;
    /** A click, or a press that never moved, anywhere on the track. */
    onTap: (tap: BarTap) => void;
    formatPeriod: (period: DensityChartPeriod) => DensityChartPeriodLabel;
}

const SLIDER_ORIENTATION: AriaAttributes["aria-orientation"] = "horizontal";

interface WindowTooltipProps {
    centerPercent: number;
    visible: { start: number; end: number };
    label: DensityChartPeriodLabel;
}

/** The period under the window while it's dragged or hovered, kept inside the bars in view. */
function WindowTooltip({
    centerPercent,
    visible,
    label: { range, duration },
}: WindowTooltipProps) {
    const tooltipRef = useRef<HTMLDivElement>(null);
    const tooltipWidth = useElementWidth(tooltipRef);
    return (
        <div
            ref={tooltipRef}
            className={styles.windowTooltip}
            style={styleWithVars({
                "--window-center": `${centerPercent}%`,
                "--window-tooltip-half-width": `${tooltipWidth / 2}px`,
                "--visible-start": `${visible.start}px`,
                "--visible-end": `${visible.end}px`,
            })}
            aria-hidden
        >
            <div className={styles.windowTooltipSurface}>
                <span className={styles.windowTooltipRange}>{range}</span>
                {duration && ` · ${duration}`}
            </div>
        </div>
    );
}

/**
 * The draggable, keyboard-adjustable window over the bars and axis, drawn
 * as an outline with a handle on each edge; it snaps to whole bars, so
 * week bars give whole weeks. Dragging across the bars outside it draws a
 * new one. The period tooltip shows while dragging or while the pointer is
 * inside the window, and is visual only, because the sliders announce the
 * same period. Positions are inline styles so a drag never re-renders the
 * bars.
 */
export function DensityChartWindowOverlay({
    buckets,
    windowSpan,
    minBar,
    strip,
    visible,
    onWindowChange,
    onWindowPreview,
    onTap,
    formatPeriod,
}: DensityChartWindowOverlayProps) {
    const trackRef = useRef<HTMLDivElement>(null);
    const [pointerBar, setPointerBar] = useState<number | null>(null);
    const barCount = buckets.length;
    const firstBar = Math.floor(minBar);
    const toWindow = useCallback(
        (span: BarSpan): DensityChartWindow => {
            const { start, end } = spanToPeriod(span, buckets);
            return { start, end };
        },
        [buckets],
    );
    const handleChange = useCallback(
        (span: BarSpan) => onWindowChange(toWindow(span)),
        [onWindowChange, toWindow],
    );
    const handlePreview = useCallback(
        (span: BarSpan | null) => onWindowPreview(span && toWindow(span)),
        [onWindowPreview, toWindow],
    );
    const {
        displaySpan,
        periodSpan,
        phase,
        pointerMode,
        handlePointerDown,
        handlePointerMove,
        handlePointerEnd,
        handlePointerCancel,
        handleKeyDown,
        consumeClickAfterDraw,
    } = useWindowDrag({
        span: windowSpan,
        barCount,
        minBar,
        strip,
        trackRef,
        onChange: handleChange,
        onBarTap: onTap,
        onPreview: handlePreview,
    });
    const shownPeriod = spanToPeriod(periodSpan, buckets);
    const periodLabel = formatPeriod(shownPeriod);
    const { range, duration } = periodLabel;
    const formatDay = (date: string) =>
        formatPeriod({ start: date, end: date, count: 1 }).range;
    const startPercent = (displaySpan.start / barCount) * 100;
    const endPercent = (displaySpan.end / barCount) * 100;
    const isPointerInWindow =
        pointerBar !== null &&
        displaySpan.start <= pointerBar &&
        pointerBar < displaySpan.end;

    const getSliderProps = (mode: WindowPart) => ({
        role: "slider",
        tabIndex: 0,
        [WINDOW_PART_ATTRIBUTE]: toWindowPart(mode),
        "aria-orientation": SLIDER_ORIENTATION,
        onPointerDown: (event: React.PointerEvent<HTMLElement>) =>
            handlePointerDown(mode, event),
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerEnd,
        onPointerCancel: handlePointerCancel,
        onKeyDown: (event: React.KeyboardEvent<HTMLElement>) =>
            handleKeyDown(mode, event),
    });

    const isOnTrack = (event: React.SyntheticEvent) =>
        event.target === event.currentTarget;
    const onTrackOnly =
        (handler: (event: React.PointerEvent<HTMLElement>) => void) =>
        (event: React.PointerEvent<HTMLElement>) => {
            if (isOnTrack(event)) {
                handler(event);
            }
        };
    const handleTrackPointerMove = onTrackOnly(handlePointerMove);
    const trackPointerProps = {
        onPointerDown: onTrackOnly((event) => handlePointerDown("draw", event)),
        onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
            const track = trackRef.current;
            if (track) {
                setPointerBar(
                    barTapAtPoint({
                        rect: track.getBoundingClientRect(),
                        point: event,
                        barCount,
                    }).barIndex,
                );
            }
            handleTrackPointerMove(event);
        },
        onPointerLeave: () => setPointerBar(null),
        onPointerUp: onTrackOnly(handlePointerEnd),
        onPointerCancel: onTrackOnly(handlePointerCancel),
    };

    const handleTrackClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const track = trackRef.current;
        if (!isOnTrack(event) || !track || consumeClickAfterDraw()) {
            return;
        }
        onTap(
            barTapAtPoint({
                rect: track.getBoundingClientRect(),
                point: event,
                barCount,
            }),
        );
    };

    return (
        <div
            className={styles.windowLayer}
            style={styleWithVars({
                "--window-stroke": `${WINDOW_STROKE_PX}px`,
            })}
            data-window-phase={phase}
        >
            <div
                ref={trackRef}
                className={styles.track}
                style={styleWithVars({
                    "--span": `${endPercent - startPercent}cqw`,
                })}
                {...trackPointerProps}
                onClick={handleTrackClick}
            >
                <div
                    {...getSliderProps("move")}
                    className={styles.windowBody}
                    style={{
                        left: `${startPercent}%`,
                        width: `${endPercent - startPercent}%`,
                    }}
                    aria-label="Move window"
                    aria-valuemin={firstBar}
                    aria-valuemax={Math.round(
                        barCount - (displaySpan.end - displaySpan.start),
                    )}
                    aria-valuenow={Math.round(displaySpan.start)}
                    aria-valuetext={duration ? `${range}, ${duration}` : range}
                />
                <div
                    className={styles.handleRail}
                    style={{ left: `${startPercent}%` }}
                >
                    <div
                        {...getSliderProps("start")}
                        className={styles.handle}
                        data-dragging={pointerMode === "start"}
                        aria-label="Window start"
                        aria-valuemin={firstBar}
                        aria-valuemax={barCount - 1}
                        aria-valuenow={clamp(
                            Math.round(displaySpan.start),
                            firstBar,
                            barCount - 1,
                        )}
                        aria-valuetext={formatDay(shownPeriod.start)}
                    />
                </div>
                <div
                    className={styles.handleRail}
                    style={{ left: `${endPercent}%` }}
                >
                    <div
                        {...getSliderProps("end")}
                        className={styles.handle}
                        data-dragging={pointerMode === "end"}
                        aria-label="Window end"
                        aria-valuemin={firstBar + 1}
                        aria-valuemax={barCount}
                        aria-valuenow={clamp(
                            Math.round(displaySpan.end),
                            firstBar + 1,
                            barCount,
                        )}
                        aria-valuetext={formatDay(shownPeriod.end)}
                    />
                </div>
            </div>
            {(phase === "dragging" || isPointerInWindow) && (
                <WindowTooltip
                    centerPercent={(startPercent + endPercent) / 2}
                    visible={visible}
                    label={periodLabel}
                />
            )}
        </div>
    );
}
